// Preis-API Adapter (REST). Passe BASE_URL/Auth nach Bedarf an.
export type PriceRequest = {
    productId: number
    categoryId?: number | null
    quantity: number
    sizeId?: string
    size?: { id?: string; widthMm?: number; heightMm?: number }
    variants: Array<{ groupId: string; optionId: string }>
}

export type PriceResponse = {
    currency: string
    price: number
    basePrice?: number
    breakdown?: Array<{ groupId: string; optionId: string; delta: number }>
    groupTotals?: Record<string, number>
    groupTotalsWithQty?: Record<string, number>
}

export type BulkPriceResponse = {
    currency: string
    total: number
    items: Array<{ key: string; index: number; price: number; basePrice?: number }>
}

const BASE_URL = 'https://www.pixelfoto-express.de/api/pricing'
const PRICE_CACHE_TTL_MS = 800
const PRICE_REQUEST_TIMEOUT_MS = 20_000

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number) {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
        return await fetch(input, {
            ...init,
            signal: ctrl.signal,
        })
    } finally {
        clearTimeout(timer)
    }
}

const inflightPriceRequests = new Map<string, Promise<any>>()
const cachedPriceRequests = new Map<string, { ts: number; value: any }>()

function stableStringify(value: any): string {
    if (Array.isArray(value)) return `[${value.map((entry) => stableStringify(entry)).join(',')}]`
    if (value && typeof value === 'object') {
        const keys = Object.keys(value).sort()
        return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
    }
    return JSON.stringify(value)
}

async function requestWithDedupe<T>(key: string, run: () => Promise<T>): Promise<T> {
    const now = Date.now()
    const cached = cachedPriceRequests.get(key)
    if (cached && now - cached.ts < PRICE_CACHE_TTL_MS) {
        return cached.value as T
    }

    const running = inflightPriceRequests.get(key)
    if (running) return running as Promise<T>

    const promise = run()
        .then((value) => {
            cachedPriceRequests.set(key, { ts: Date.now(), value })
            return value
        })
        .finally(() => {
            inflightPriceRequests.delete(key)
        })

    inflightPriceRequests.set(key, promise)
    return promise
}

export async function quote(req: PriceRequest): Promise<PriceResponse> {
    const body = JSON.stringify(req)
    const key = `quote:${stableStringify(req)}`
    return requestWithDedupe(key, async () => {
        const res = await fetchWithTimeout(`${BASE_URL}/quote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body,
        }, PRICE_REQUEST_TIMEOUT_MS)
        if (!res.ok) throw new Error(`Pricing error ${res.status}`)
        return res.json()
    })
}

export async function quoteMany(
    baseReq: PriceRequest,
    overrides: Array<{ key: string; variants?: PriceRequest['variants']; size?: PriceRequest['size'] }>
): Promise<Record<string, PriceResponse>> {
    const payload = { base: baseReq, overrides }
    const body = JSON.stringify(payload)
    const key = `quote-many:${stableStringify(payload)}`
    return requestWithDedupe(key, async () => {
        const res = await fetchWithTimeout(`${BASE_URL}/quote-many`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body,
        }, PRICE_REQUEST_TIMEOUT_MS)
        if (!res.ok) throw new Error(`Pricing error ${res.status}`)
        return res.json()
    })
}

export async function quoteBulk(items: Array<PriceRequest & { key?: string }>): Promise<BulkPriceResponse> {
    const payload = { items }
    const body = JSON.stringify(payload)
    const key = `quote-bulk:${stableStringify(payload)}`
    return requestWithDedupe(key, async () => {
        const res = await fetchWithTimeout(`${BASE_URL}/quote-bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body,
        }, PRICE_REQUEST_TIMEOUT_MS)
        if (!res.ok) throw new Error(`Pricing error ${res.status}`)
        return res.json()
    })
}
